// Sign in (spec C7.1): email, password with show and hide, Sign in, Forgot password?
// Markup from design/kit/components/SignInCard and the "Sign in and system states" board.
import { useRef, useState, type FormEvent } from 'react';
import { useLoaderData, useLocation, useNavigate, useSearchParams } from 'react-router';
import type { Api } from '../auth/api';
import { copy, noticeText, signInFailure, type SignInNotice } from '../auth/copy';
import { safeNext, withNext } from '../auth/next';
import { AmberLine, Brand, ErrorLine, PasswordField, SignInLayout } from '../auth/SignInLayout';
import { AlertCircleIcon, ClockIcon } from '../shell/icons';
import { Toast } from '../shell/Toast';

export type SignInLoaderData = { expiredEmail: string | null };

/** What other screens pass to Sign in in the navigation state. */
export type SignInState = { email?: string; toast?: string; notice?: SignInNotice } | null;

export function SignInPage({ api }: { api: Api }) {
  const { expiredEmail } = useLoaderData() as SignInLoaderData;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const state = useLocation().state as SignInState;
  const expired = params.get('expired') === '1';
  const next = safeNext(params.get('next'));

  const [email, setEmail] = useState(state?.email ?? expiredEmail ?? '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(
    state?.notice && state.notice !== 'disabled' ? noticeText[state.notice] : null,
  );
  const [disabled, setDisabled] = useState(state?.notice === 'disabled');
  const passwordRef = useRef<HTMLInputElement>(null);

  const canSubmit = email.trim() !== '' && password !== '' && !busy;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setDisabled(false);
    const res = await api.signIn(email.trim(), password);
    if (res.ok) {
      if (res.body.next === 'done') {
        navigate(next, { replace: true });
      } else {
        navigate(withNext('/sign-in/code', next), {
          state: { email: email.trim(), resendIn: res.body.resendIn },
        });
      }
      return;
    }
    setBusy(false);
    setPassword('');
    const shown = signInFailure(res);
    if (shown.disabled) setDisabled(true);
    else setError(shown.text);
    passwordRef.current?.focus();
  }

  const titleId = expired ? 'signin-expired' : 'signin-title';

  return (
    <SignInLayout
      footnote={expired ? undefined : copy.footnote}
      toast={state?.toast ? <Toast text={state.toast} /> : undefined}
    >
      <form className="lcard" aria-labelledby={titleId} noValidate onSubmit={submit}>
        <Brand />
        {expired ? (
          <div className="gbanner">
            <ClockIcon />
            <span id={titleId}>{copy.expired}</span>
          </div>
        ) : (
          <h1 id={titleId} className="sr">
            {copy.signIn}
          </h1>
        )}
        <div className="lf">
          <label htmlFor="signin-email">Email</label>
          <input
            id="signin-email"
            className="input"
            type="email"
            autoComplete="username"
            placeholder="you@safeside.example"
            autoFocus={!email}
            maxLength={254}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <PasswordField
          id="signin-password"
          label="Password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          inputRef={passwordRef}
          autoFocus={!!email}
          invalid={!!error}
        />
        {error && <ErrorLine text={error} />}
        {disabled && <AmberLine role="alert" icon={<AlertCircleIcon />} text={copy.disabled} />}
        <button type="submit" className="btn btn-primary wide" disabled={!canSubmit}>
          {busy ? copy.signingIn : copy.signIn}
        </button>
        {expired ? (
          <p className="m0 t12 muted" style={{ textAlign: 'center' }}>
            {copy.goBack}
          </p>
        ) : (
          <div className="row" style={{ justifyContent: 'center' }}>
            {/* Forgot password keeps the email typed here. */}
            <button
              type="button"
              className="linkbtn"
              onClick={() => navigate('/forgot-password', { state: { email: email.trim() } })}
            >
              {copy.forgot}
            </button>
          </div>
        )}
      </form>
    </SignInLayout>
  );
}
